/**
 * DemocratCharts — replay state.
 *
 * Holds the bar-replay machine for the current chart:
 *   - active        — are we in replay mode at all?
 *   - setupMode     — user is choosing the start candle (waiting for click)
 *   - startIndex    — index in the candle array where replay begins
 *   - currentIndex  — currently revealed candle (incremented while playing)
 *   - playing       — auto-advance timer running
 *   - speed         — multiplier (1x = 1 candle/sec)
 *
 * Bookmarks persist to localStorage. Per-symbol so you can have separate
 * replay setups across instruments.
 */

import { create } from "zustand";

export const SPEEDS = [0.1, 0.3, 0.5, 1, 3, 10] as const;
export type Speed = (typeof SPEEDS)[number];

export interface Bookmark {
  id: string;
  symbol: string;
  exchange: string;
  timeframe: string;
  candleTs: string; // ISO of the bookmarked candle
  candleIndex: number;
  label?: string;
  createdAt: string;
}

interface ReplayState {
  active: boolean;
  setupMode: boolean;
  startIndex: number | null;
  currentIndex: number | null;
  playing: boolean;
  speed: Speed;
  totalCandles: number;
  bookmarks: Bookmark[];

  // ── Actions ────────────────────────────────────────────────────────────
  enter: () => void;
  exit: () => void;
  setStartIndex: (idx: number) => void;
  setTotalCandles: (n: number) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  stepForward: () => void;
  stepBackward: () => void;
  setSpeed: (s: Speed) => void;
  bumpSpeedUp: () => void;
  bumpSpeedDown: () => void;
  reset: () => void;

  // Bookmarks
  addBookmark: (params: {
    symbol: string;
    exchange: string;
    timeframe: string;
    candleTs: string;
    label?: string;
  }) => void;
  removeBookmark: (id: string) => void;
  jumpToBookmark: (id: string, candles: { ts: string }[]) => void;
}

const BOOKMARKS_KEY = "livecharts:bookmarks";

function loadBookmarks(): Bookmark[] {
  try {
    const raw = localStorage.getItem(BOOKMARKS_KEY);
    return raw ? (JSON.parse(raw) as Bookmark[]) : [];
  } catch {
    return [];
  }
}

function saveBookmarks(bookmarks: Bookmark[]) {
  try {
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarks));
  } catch {
    /* localStorage full or disabled — non-fatal */
  }
}

export const useReplayStore = create<ReplayState>((set, get) => ({
  active: false,
  setupMode: false,
  startIndex: null,
  currentIndex: null,
  playing: false,
  speed: 1,
  totalCandles: 0,
  bookmarks: loadBookmarks(),

  enter: () => set({ active: true, setupMode: true, playing: false }),

  exit: () =>
    set({
      active: false,
      setupMode: false,
      startIndex: null,
      currentIndex: null,
      playing: false,
    }),

  setStartIndex: (idx) =>
    set({
      startIndex: idx,
      currentIndex: idx,
      setupMode: false,
      playing: false,
    }),

  setTotalCandles: (n) => set({ totalCandles: n }),

  play: () => {
    const { currentIndex, totalCandles, active } = get();
    if (!active || currentIndex === null) return;
    if (currentIndex >= totalCandles - 1) return; // nothing left to reveal
    set({ playing: true });
  },

  pause: () => set({ playing: false }),

  togglePlay: () => {
    const { playing, play, pause } = get();
    playing ? pause() : play();
  },

  stepForward: () => {
    const { currentIndex, totalCandles, active } = get();
    if (!active || currentIndex === null) return;
    if (currentIndex >= totalCandles - 1) {
      set({ playing: false });
      return;
    }
    set({ currentIndex: currentIndex + 1 });
  },

  stepBackward: () => {
    const { currentIndex, startIndex, active } = get();
    if (!active || currentIndex === null || startIndex === null) return;
    if (currentIndex <= startIndex) return;
    set({ currentIndex: currentIndex - 1, playing: false });
  },

  setSpeed: (s) => set({ speed: s }),

  bumpSpeedUp: () => {
    const cur = get().speed;
    const i = SPEEDS.indexOf(cur);
    if (i < SPEEDS.length - 1) set({ speed: SPEEDS[i + 1] });
  },

  bumpSpeedDown: () => {
    const cur = get().speed;
    const i = SPEEDS.indexOf(cur);
    if (i > 0) set({ speed: SPEEDS[i - 1] });
  },

  reset: () =>
    set({
      active: false,
      setupMode: false,
      startIndex: null,
      currentIndex: null,
      playing: false,
      totalCandles: 0,
    }),

  addBookmark: ({ symbol, exchange, timeframe, candleTs, label }) => {
    const { currentIndex, bookmarks } = get();
    if (currentIndex === null) return;
    const bm: Bookmark = {
      id: `bm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      symbol,
      exchange,
      timeframe,
      candleTs,
      candleIndex: currentIndex,
      label,
      createdAt: new Date().toISOString(),
    };
    const next = [bm, ...bookmarks].slice(0, 100); // cap at 100
    saveBookmarks(next);
    set({ bookmarks: next });
  },

  removeBookmark: (id) => {
    const next = get().bookmarks.filter((b) => b.id !== id);
    saveBookmarks(next);
    set({ bookmarks: next });
  },

  jumpToBookmark: (id, candles) => {
    const bm = get().bookmarks.find((b) => b.id === id);
    if (!bm) return;
    // Find the matching candle by timestamp (handles symbol/timeframe mismatches gracefully)
    const idx = candles.findIndex((c) => c.ts === bm.candleTs);
    if (idx < 0) return;
    set({
      active: true,
      setupMode: false,
      startIndex: idx,
      currentIndex: idx,
      playing: false,
    });
  },
}));
